import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import {
  FilePdf, CheckCircle, ShieldCheck, ClockCountdown, ArrowLeft, ChatCircleDots,
} from "@phosphor-icons/react";

export default function RentalAgreementPage() {
  const { rentalId } = useParams();
  const { user } = useAuth();
  const [rental, setRental] = useState(null);
  const [agreement, setAgreement] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [pdfBust, setPdfBust] = useState(0);

  const load = async () => {
    try {
      const [r, a] = await Promise.all([
        api.get(`/rentals/${rentalId}`),
        api.get(`/rentals/${rentalId}/agreement`),
      ]);
      setRental(r.data);
      setAgreement(a.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load agreement");
    }
  };

  useEffect(() => { load(); }, [rentalId]);

  const myRole = rental && user
    ? (rental.owner_id === user.id ? "owner" : rental.user_id === user.id ? "tenant" : null)
    : null;

  const alreadySigned = agreement?.signatures?.[myRole];

  const accept = async () => {
    if (!myRole) return;
    setAccepting(true);
    try {
      await api.post(`/rentals/${rentalId}/agreement/accept`, { role: myRole });
      toast.success("Agreement accepted ✓");
      setPdfBust((v) => v + 1);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to accept");
    } finally {
      setAccepting(false);
    }
  };

  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/rentals/${rentalId}/agreement.pdf?v=${pdfBust}`;

  if (!rental || !agreement) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statusChip = {
    draft: { label: "Draft — awaiting signatures", cls: "bg-neutral-100 text-neutral-700 border-neutral-300" },
    pending: { label: "Pending — awaiting other party", cls: "bg-amber-50 text-amber-800 border-amber-300" },
    signed: { label: "Signed by both parties", cls: "bg-emerald-50 text-emerald-800 border-emerald-300" },
  }[agreement.status] || { label: agreement.status, cls: "bg-neutral-100" };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12" data-testid="agreement-page">
      <Link to="/my-rentals" className="text-xs text-neutral-500 hover:text-midnight inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> Back to My Rentals
      </Link>
      <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">Digital agreement</p>
      <h1 className="font-serif text-4xl text-midnight mb-2">Rental Agreement</h1>
      <p className="text-sm text-neutral-500 mb-8">
        {rental.property_title} · {rental.property_city} · {rental.lease_months} months
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs ${statusChip.cls}`} data-testid="agreement-status">
            {agreement.status === "signed" ? <CheckCircle size={14} weight="fill" /> : <ClockCountdown size={14} weight="duotone" />}
            {statusChip.label}
          </div>

          <div className="border border-black/10 rounded-2xl overflow-hidden bg-white" style={{ height: 720 }}>
            <iframe
              src={pdfUrl}
              title="Rental Agreement PDF"
              className="w-full h-full"
              data-testid="agreement-pdf-frame"
            />
          </div>

          <a href={pdfUrl} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-2 text-sm text-gold hover:underline" data-testid="pdf-download-link">
            <FilePdf size={18} weight="duotone" /> Download PDF
          </a>
        </div>

        <aside className="space-y-4">
          <div className="bg-white border border-black/10 rounded-2xl p-6 sticky top-28">
            <h3 className="font-serif text-xl text-midnight mb-4">Signatures</h3>

            <SignatureRow role="Owner" sig={agreement.signatures?.owner} name={agreement.owner_name} />
            <SignatureRow role="Tenant" sig={agreement.signatures?.tenant} name={agreement.tenant_name} />

            {myRole && !alreadySigned && (
              <Button onClick={accept} disabled={accepting}
                className="w-full mt-6 bg-midnight hover:bg-midnight-hover text-bone py-6"
                data-testid="accept-agreement-btn">
                <ShieldCheck size={18} weight="duotone" className="mr-2" />
                {accepting ? "Signing..." : `Accept & Sign as ${myRole === "owner" ? "Owner" : "Tenant"}`}
              </Button>
            )}
            {alreadySigned && (
              <div className="mt-6 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-sm text-emerald-800 flex items-center gap-2">
                <CheckCircle size={18} weight="fill" /> You signed on {new Date(alreadySigned.signed_at).toLocaleDateString("en-IN")}
              </div>
            )}

            {agreement.status === "signed" && (
              <p className="text-[11px] text-emerald-700 mt-3">
                🛡️ Fully executed under IT Act § 10A. Both parties bound.
              </p>
            )}

            {agreement.digio_configured && (
              <p className="text-[11px] text-neutral-500 mt-3">
                For Aadhaar-linked eSign, contact admin to trigger Digio flow.
              </p>
            )}

            <Link to={`/chat/${rentalId}`}
                  className="mt-4 flex items-center justify-center gap-2 w-full py-2.5 rounded-full border border-black/10 hover:bg-stone text-sm"
                  data-testid="open-chat-btn">
              <ChatCircleDots size={16} weight="duotone" /> Open Chat
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}

function SignatureRow({ role, sig, name }) {
  return (
    <div className={`p-3 rounded-xl border mb-3 ${sig ? "bg-emerald-50 border-emerald-200" : "bg-neutral-50 border-neutral-200"}`}
         data-testid={`sig-${role.toLowerCase()}`}>
      <p className="text-[10px] tracking-widest uppercase text-neutral-500">{role}</p>
      {sig ? (
        <>
          <p className="text-sm font-medium text-midnight mt-0.5">{sig.name}</p>
          <p className="text-[11px] text-neutral-500">
            {new Date(sig.signed_at).toLocaleString("en-IN")} · IP {sig.ip}
          </p>
        </>
      ) : (
        <p className="text-sm text-neutral-500 mt-0.5">{name || "—"} · Pending</p>
      )}
    </div>
  );
}
